// Das Bankkonto.
//
// Noch ist keine Bank angebunden — das Modul steht bereit, es fließt nur noch
// nichts hinein. Die Seite sagt das auch so. Eine leere Liste ohne Erklärung
// hieße "keine Umsätze", und das ist etwas völlig anderes als "nicht
// angeschlossen": Das eine ist ein ruhiger Monat, das andere ein Defekt.
//
// Der Aktualisieren-Knopf holt das, was da ist, frisch aus der Datenbank. Er
// behauptet NICHT, mit der Bank gesprochen zu haben — solange keine verbunden
// ist, steht das daneben.

import { useMemo, useState } from "react";
import {
  useBankVerbunden,
  useKontobewegungen,
  useOffeneRechnungen,
  useZuordnen,
  useZuordnungLoesen,
  summen,
  type BewegungsFilter,
  type Kontobewegung,
} from "@/lib/bank";
import { LogoLoader } from "@/components/LogoLoader";
import {
  Card,
  EmptyState,
  Input,
  Segmented,
  Sheet,
  cn,
  euro,
} from "@/design-system/ui";
import { ModulHeader } from "@/modules/home/ModulHeader";
import { useQueryClient } from "@tanstack/react-query";

const FILTER: { value: BewegungsFilter; label: string }[] = [
  { value: "alle", label: "Alle" },
  { value: "eingang", label: "Eingang" },
  { value: "ausgang", label: "Ausgang" },
  { value: "offen", label: "Offen" },
];

export default function BankPage() {
  const [filter, setFilter] = useState<BewegungsFilter>("alle");
  const [zuordnen, setZuordnen] = useState<Kontobewegung | null>(null);

  const { data: konten = [], isLoading: kontenLaden } = useBankVerbunden();
  const {
    data: bewegungen = [],
    isLoading,
    isFetching,
  } = useKontobewegungen(filter);
  const qc = useQueryClient();

  const verbunden = konten.length > 0;
  const s = useMemo(() => summen(bewegungen), [bewegungen]);

  // Kein Bank-Abruf, kein So-tun-als-ob: Der Knopf lädt die Tabelle neu. Sobald
  // eine Bank hängt, ruft er zusätzlich den Abruf an — die Stelle ist dieselbe.
  const aktualisieren = () => {
    void qc.invalidateQueries({ queryKey: ["kontobewegungen"] });
    void qc.invalidateQueries({ queryKey: ["bankkonten"] });
  };

  if (isLoading || kontenLaden) return <LogoLoader />;

  return (
    <div className="mx-auto flex h-full max-w-2xl flex-col">
      <ModulHeader
        titel="Bank"
        untertitel={
          verbunden
            ? `${bewegungen.length} Bewegungen${s.offen ? ` · ${s.offen} nicht zugeordnet` : ""}`
            : "keine Bank verbunden"
        }
        aktion={
          <button
            onClick={aktualisieren}
            aria-label="Aktualisieren"
            className="flex h-11 w-11 items-center justify-center rounded-lg text-ink-400 transition-colors duration-fast hover:bg-ink-800 hover:text-ink-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gruen-500/60"
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              className={cn(isFetching && "animate-spin")}
              aria-hidden
            >
              <path d="M21 12a9 9 0 1 1-2.64-6.36" />
              <path d="M21 3v6h-6" />
            </svg>
          </button>
        }
      />

      <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-6 pt-4">
        {/* Keine Bank verbunden ist KEIN Fehler — es ist der Normalzustand, bis die
            Anbindung steht. Also auch keine schreiende Warnfläche: ein ruhiger
            Kasten mit einem kleinen Signalpunkt. Wer hier jeden Tag reinschaut,
            soll nicht jeden Tag angebrüllt werden.

            Bewusst ein eigenes div statt <Card className="bg-…">: `cn()` ist ein
            reiner String-Join ohne tailwind-merge — zwei Hintergrundklassen
            landeten beide im DOM, und die CSS-Reihenfolge entschiede. */}
        {!verbunden && (
          <div className="mb-4 flex gap-3 rounded-xl border border-ink-700/70 bg-ink-800/60 px-3.5 py-3">
            <span
              className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-warn-fg"
              aria-hidden
            />
            <div>
              <p className="text-[15px] font-medium text-ink-100">
                Noch keine Bank verbunden
              </p>
              <p className="mt-0.5 text-sm text-ink-400">
                Das Modul ist fertig. Sobald die Kontoanbindung steht,
                erscheinen die Umsätze hier von selbst und lassen sich den
                Rechnungen zuordnen.
              </p>
            </div>
          </div>
        )}

        {/* Summen und Filter erst, wenn es etwas zu summieren und zu filtern gibt.
            Ein Filter über einer leeren Liste ist nur Zierrat. */}
        {verbunden && (
          <>
            <div className="mb-4 grid grid-cols-3 gap-2">
              <Summe label="Eingang" wert={s.ein} ton="gut" />
              <Summe label="Ausgang" wert={-s.aus} ton="schlecht" />
              <Summe
                label="Saldo"
                wert={s.saldo}
                ton={s.saldo >= 0 ? "gut" : "schlecht"}
              />
            </div>

            <div className="mb-4">
              <Segmented value={filter} onChange={setFilter} options={FILTER} />
            </div>
          </>
        )}

        {bewegungen.length > 0 ? (
          <ul className="space-y-2">
            {bewegungen.map((b) => (
              <BewegungsZeile
                key={b.id}
                b={b}
                onZuordnen={() => setZuordnen(b)}
              />
            ))}
          </ul>
        ) : (
          // Ohne Bank sagt der Kasten oben schon alles — das hier wäre die dritte
          // Wiederholung derselben Aussage auf einem Schirm.
          verbunden && (
            <EmptyState
              title="Keine Bewegungen"
              hint="Für diesen Filter gibt es nichts."
            />
          )
        )}
      </div>

      <ZuordnenBlatt bewegung={zuordnen} onClose={() => setZuordnen(null)} />
    </div>
  );
}

function Summe({
  label,
  wert,
  ton,
}: {
  label: string;
  wert: number;
  ton: "gut" | "schlecht";
}) {
  return (
    <Card className="px-3 py-2.5">
      <p className="text-xs uppercase tracking-wider text-ink-500">{label}</p>
      <p
        className={cn(
          "mt-0.5 text-[15px] font-semibold tabular-nums",
          ton === "gut" ? "text-gruen-400" : "text-danger-fg",
        )}
      >
        {euro(wert)}
      </p>
    </Card>
  );
}

function BewegungsZeile({
  b,
  onZuordnen,
}: {
  b: Kontobewegung;
  onZuordnen: () => void;
}) {
  const loesen = useZuordnungLoesen();
  const eingang = b.amount_cents > 0;
  const zugeordnet = !!b.matched_invoice_id && !!b.rechnung;

  // Links der Text, rechts eine schmale Spalte mit Betrag UND Knopf untereinander.
  //
  // Nebeneinander frisst beides zusammen über die Hälfte der Zeile, und
  // "Futtermittel Wenner GmbH" wird zu "Futtermittel We…". Untereinander kostet
  // es keine Höhe — die linke Seite hat ohnehin drei Zeilen — und der Name
  // bleibt lesbar.
  const links = (
    <div className="min-w-0 flex-1">
      <p className="truncate text-[15px] font-medium text-ink-100">
        {b.counterparty ?? "Unbekannt"}
      </p>
      {b.purpose && (
        <p className="truncate text-sm text-ink-400">{b.purpose}</p>
      )}

      <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1">
        <span className="text-xs tabular-nums text-ink-500">
          {new Date(b.booking_date).toLocaleDateString("de-DE")}
        </span>
        {zugeordnet && (
          // shrink-0, nicht truncate: Die Rechnungsnummer ist der ganze Zweck des
          // Badges. Ein "R2601…" beantwortet die Frage nicht, wegen der man
          // hinsieht.
          <span className="shrink-0 rounded-full bg-gruen-500/15 px-2 py-0.5 text-xs font-medium text-gruen-400">
            {b.rechnung!.invoice_number}
            {b.hat_rechnung_bezahlt && " · bezahlt"}
          </span>
        )}
      </div>
    </div>
  );

  const betrag = (
    <p
      className={cn(
        "text-[15px] font-semibold tabular-nums",
        eingang ? "text-gruen-400" : "text-danger-fg",
      )}
    >
      {euro(b.amount_cents)}
    </p>
  );

  const knopfStil =
    "rounded-lg border border-ink-700 px-2.5 py-1.5 text-sm font-medium text-ink-300";

  // Nicht zugeordnet: die GANZE Zeile öffnet das Blatt.
  //
  // Ein kleiner Knopf am Rand wäre unter 44px und damit auf dem Handy ein
  // Glücksspiel. Die Zeile selbst ist das Touch-Ziel — der Knopf zeigt nur, dass
  // sie eins ist.
  if (!zugeordnet) {
    return (
      <li>
        <button
          onClick={onZuordnen}
          className="flex w-full items-center gap-3 rounded-xl border border-ink-700/70 bg-ink-900 px-3.5 py-3 text-left transition-colors duration-fast hover:bg-ink-850 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gruen-500/60"
        >
          {links}
          <span className="flex shrink-0 flex-col items-end gap-1.5">
            {betrag}
            <span className={knopfStil} aria-hidden>
              Zuordnen
            </span>
          </span>
        </button>
      </li>
    );
  }

  // Zugeordnet: hier darf die Zeile NICHT als Ganzes klickbar sein — sonst löst
  // ein Daumen beim Scrollen eine Zuordnung, die Geld bewegt hat.
  return (
    <li className="flex items-center gap-3 rounded-xl border border-ink-700/70 bg-ink-900 px-3.5 py-3">
      {links}
      <div className="flex shrink-0 flex-col items-end gap-1.5">
        {betrag}
        {/* Gleiche Form wie "Zuordnen" — zwei Knöpfe in derselben Liste, die
            unterschiedlich aussehen, wirken wie ein Versehen. */}
        <button
          onClick={() => loesen.mutate(b)}
          disabled={loesen.isPending}
          className={cn(
            knopfStil,
            "transition-colors duration-fast hover:bg-ink-800 hover:text-ink-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gruen-500/60 disabled:opacity-50",
          )}
        >
          Lösen
        </button>
      </div>
    </li>
  );
}

function ZuordnenBlatt({
  bewegung,
  onClose,
}: {
  bewegung: Kontobewegung | null;
  onClose: () => void;
}) {
  const [suche, setSuche] = useState("");
  const { data: rechnungen = [] } = useOffeneRechnungen(suche);
  const zuordnen = useZuordnen();

  return (
    <Sheet open={!!bewegung} onClose={onClose} title="Beleg zuordnen">
      {bewegung && (
        <div className="space-y-4">
          <Card className="px-3.5 py-3">
            <p className="text-[15px] font-medium text-ink-100">
              {bewegung.counterparty ?? "Unbekannt"}
            </p>
            {bewegung.purpose && (
              <p className="text-sm text-ink-400">{bewegung.purpose}</p>
            )}
            <p className="mt-1 text-[15px] font-semibold tabular-nums text-ink-50">
              {euro(bewegung.amount_cents)}
            </p>
          </Card>

          <Input
            value={suche}
            onChange={(e) => setSuche(e.target.value)}
            placeholder="Rechnungsnummer suchen"
          />

          {rechnungen.length === 0 ? (
            <EmptyState
              title="Keine offene Rechnung"
              hint="Es gibt nichts zuzuordnen."
            />
          ) : (
            <ul className="space-y-2">
              {rechnungen.map((r) => {
                const passt = r.total_cents === bewegung.amount_cents;
                return (
                  <li key={r.id}>
                    <button
                      onClick={() =>
                        zuordnen.mutate(
                          { bewegung, rechnung: r },
                          { onSuccess: onClose },
                        )
                      }
                      disabled={zuordnen.isPending}
                      className="flex w-full items-center gap-3 rounded-xl border border-ink-700/70 bg-ink-900 px-3.5 py-3 text-left transition-colors duration-fast hover:bg-ink-850 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gruen-500/60 disabled:opacity-50"
                    >
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-[15px] font-medium text-ink-100">
                          {r.invoice_number}
                        </p>
                        <p className="truncate text-sm text-ink-400">
                          {(r.customer as { display_name?: string } | null)
                            ?.display_name ?? "—"}
                        </p>
                      </div>

                      <div className="shrink-0 text-right">
                        <p className="text-[15px] font-semibold tabular-nums text-ink-50">
                          {euro(r.total_cents)}
                        </p>
                        {/* Der Betrag entscheidet, ob die Rechnung mit der
                            Zuordnung auch bezahlt gebucht wird. Das muss man
                            VORHER sehen, nicht hinterher erklärt bekommen. */}
                        <p
                          className={cn(
                            "text-xs",
                            passt ? "text-gruen-400" : "text-warn-fg",
                          )}
                        >
                          {passt ? "Betrag passt" : "Betrag weicht ab"}
                        </p>
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}

          <p className="text-xs text-ink-500">
            Stimmt der Betrag genau überein, wird die Rechnung mit der Zuordnung
            auch als bezahlt gebucht. Weicht er ab, wird nur verknüpft — über
            den Rest entscheidet der Mensch.
          </p>
        </div>
      )}
    </Sheet>
  );
}
